import { useQuery } from '@apollo/client';
import { faInfoCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { DiagramParentType } from '~/apollo/generated/v4/graphql';
import { Panel, SimplePanel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { DiagramRendererV4 } from '~/components/diagram/diagram-renderer-v4';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { CollapsibleCard } from '~/components/ui/collapse';
import { DeleteDiagram } from '~/components/upload/diagram/delete-diagram';
import { DiagramEditor } from '~/components/upload/diagram/diagram-editor';
import { UploadDiagramImage } from '~/components/upload/diagram/upload-diagram-image';
import { useLithostratTypeUpdateOutletContext } from '~/routes/upload/lithostrat-type/$lithostratTypeId';
import { createRefetchQueries } from '~/utils/graphql';

const UPLOAD_LITHOSTRAT_TYPE_UPDATE_DIAGRAM_PAGE = graphql(`
  query UploadLithostratTypeUpdateDiagramPage($id: ID!) {
    lithostratTypeGet(id: $id) {
      id
      diagram {
        ...diagramEditorParts
      }
    }
  }
`);

export default function UploadLithostratTypeUpdateDiagramPage() {
  return (
    <ApolloProviderV4>
      <UploadLithostratTypeUpdateDiagramPageInner />
    </ApolloProviderV4>
  );
}

function UploadLithostratTypeUpdateDiagramPageInner() {
  const ctx = useLithostratTypeUpdateOutletContext();

  const { data, loading } = useQuery(
    UPLOAD_LITHOSTRAT_TYPE_UPDATE_DIAGRAM_PAGE,
    { variables: { id: ctx.lithostratType.id } },
  );

  const refetchQueries = createRefetchQueries([
    {
      query: UPLOAD_LITHOSTRAT_TYPE_UPDATE_DIAGRAM_PAGE,
      variables: { id: ctx.lithostratType.id },
    },
  ]);

  const lithostratType = data?.lithostratTypeGet;
  const diagram = lithostratType?.diagram;

  if (loading) return <SpinnerPlaceholder />;
  invariant(lithostratType, 'Error loading lithostrat type');

  if (!diagram) {
    return (
      <div className="lg:w-1/2 mx-auto">
        <SimplePanel title="Create a Diagram">
          <div className="space-y-2">
            <Alert>
              <FontAwesomeIcon icon={faInfoCircle} />
              <div>
                This lithostrat type doesn't have a diagram yet. Get started by
                uploading an image below.
              </div>
            </Alert>

            <UploadDiagramImage
              parentType={DiagramParentType.LithostratType}
              parentId={+lithostratType.id}
              refetchQueries={refetchQueries}
            />
          </div>
        </SimplePanel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DiagramEditor diagram={diagram} />

      <Panel>
        <Panel.Heading>
          <Panel.Title>Preview</Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          <DiagramRendererV4 diagram={diagram} />
        </Panel.Body>
      </Panel>

      <CollapsibleCard title="Delete Diagram">
        <div className="text-center">
          <DeleteDiagram diagramId={diagram.id} refetchQueries={refetchQueries}>
            {confirmDelete => (
              <Button
                type="button"
                onClick={confirmDelete}
                color="error"
                startIcon={<FontAwesomeIcon icon={faTrash} />}
              >
                Delete Diagram
              </Button>
            )}
          </DeleteDiagram>
        </div>
      </CollapsibleCard>
    </div>
  );
}
